import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import HeaderBar from './components/HeaderBar';
import FilterStrip from './components/FilterStrip';
import PerformanceSummary from './components/PerformanceSummary';
import KPIGrid from './components/KPIGrid';
import TrendPanel from './components/TrendPanel';
import RankedModule from './components/RankedModule';
import MiniMultiples from './components/MiniMultiples';
import DrillDrawer from './components/DrillDrawer';
import ExcelUploader from './components/ExcelUploader';
import ClearDatabaseModal from './components/ClearDatabaseModal';
import AdminPanel from './components/AdminPanel';
import useAdminKeySequence from './hooks/useAdminKeySequence';
import useDashboardStore from './store/dashboardStore';
import './App.css';

function App() {
  const { init, loading, error, toggleDrillDrawer, uploadModalOpen, toggleUploadModal, loadData } = useDashboardStore();
  const { isAdminTriggered, resetAdminTrigger } = useAdminKeySequence();
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);

  useEffect(() => {
    init();
  }, []);

  // Handle admin trigger
  useEffect(() => {
    if (isAdminTriggered) {
      setAdminPanelOpen(true);
      resetAdminTrigger();
    }
  }, [isAdminTriggered, resetAdminTrigger]);

  const handleUploadSuccess = () => {
    loadData(); // Refresh data after successful upload
  };

  const handleCloseAdminPanel = () => {
    setAdminPanelOpen(false);
  };

  // Show loading for dashboard data
  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <motion.div
          className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-brand-bad text-lg mb-4">Error loading dashboard</p>
          <p className="text-brand-muted">{error}</p>
        </div>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="min-h-screen bg-brand-bg">
      <HeaderBar />
      <FilterStrip />

      {/* Performance Summary - New section */}
      <PerformanceSummary />

      <main className="max-w-7xl mx-auto">
        {/* KPI Grid - Now includes Profit Ratio */}
        <KPIGrid />

        {/* Main Content Row - Now 2 columns instead of 3 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-6 py-4">
          <div>
            <RankedModule />
          </div>
          <div>
            <TrendPanel />
          </div>
        </div>

        {/* Mini Multiples */}
        <div className="px-6 py-4">
          <MiniMultiples />
        </div>

        {/* Drill Details Button */}
        <div className="px-6 py-4">
          <motion.button
            onClick={toggleDrillDrawer}
            className="w-full bg-brand-card hover:bg-brand-card/80 rounded-card p-4 text-brand-fg transition-colors border border-brand-muted2/20"
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <div className="flex items-center justify-center space-x-2">
              <span className="text-sm font-medium">View Detailed Data</span>
              <span className="text-xs text-brand-muted">(Includes all records)</span>
            </div>
          </motion.button>
        </div>
      </main>

      <DrillDrawer />
      <ExcelUploader
        isOpen={uploadModalOpen}
        onClose={toggleUploadModal}
        onSuccess={handleUploadSuccess}
      />
      <ClearDatabaseModal />
      
      {/* Hidden Admin Panel */}
      <AdminPanel
        isOpen={adminPanelOpen}
        onClose={handleCloseAdminPanel}
      />
    </div>
  );
}

export default App;